import { convert } from "@volley/physics/compat";
import { PlayMode } from "@volley/shared/apps/app-common-models";
import { CuratedWorkoutShot } from "@volley/shared/apps/curated-workout-models";
import { SingleShotShot } from "@volley/shared/apps/single-shot-models";
import { Side } from "@volley/shared/http/side";
import type { Position as PositionWithHeight } from "@volley/shared/http/trainer-control";

import {
    Coord,
    CoordWithSys,
    PositionLike,
} from "../../../util/position-types";
import { COURT_WIDTH_MM } from "../../../util/positionUtil";
import { Sport } from "../../common/context/sport";

import { HitBox, ScaledDimensions } from "./types";

export function areEqual(
    a: PositionLike | undefined,
    b: PositionLike | undefined,
) {
    return a?.x === b?.x && a?.y === b?.y && a?.yaw === b?.yaw;
}

/*
    Given a coordinate in either court (mm) or physics (m) and a court width (m),
    calculate:
    - a: the distance from that point to the opposite baseline
    - b: the distance from where a intersects the baseline to the midpoint on the baseline
    - c: the hypotenuse of the triangle formed by a and b
    - yaw: the yaw required to point the trainer at the center of the opposite baseline
    Last, depending on which side of the court we're on, we may need to return a negative value
*/
export function yawToBaseline(
    coord: CoordWithSys,
    width: number,
    height: number,
) {
    const { x, y, sys } = coord;
    let w = width;
    let h = height;
    if (sys === "court") {
        // the values need to be converted from m to mm
        w = width * 1000;
        h = height * 1000;
    }
    // distance to opposite baseline
    const a = sys === "court" ? y : h / 2 - y;
    const b = sys === "court" ? Math.abs(x - w / 2) : Math.abs(x);

    // yaw is calculated as the inverse sin of side b divided by side c
    const c = Math.sqrt(a ** 2 + b ** 2);
    const yaw = Math.asin(b / c);
    if (sys === "court") {
        if (x > w / 2) {
            return -yaw;
        }
        return yaw;
    }

    if (x > 0) {
        return -yaw;
    }

    return yaw;
}

export function pointInPolygon(x: number, y: number, hitBox: HitBox) {
    const xmin = hitBox.x - hitBox.w / 2;
    const xmax = hitBox.x + hitBox.w / 2;
    const ymin = hitBox.y - hitBox.h / 2;
    const ymax = hitBox.y + hitBox.h / 2;

    return x >= xmin && x <= xmax && y >= ymin && y <= ymax;
}

export function projected2Image(
    coord: CoordWithSys,
    scaled: ScaledDimensions,
): Coord {
    const { x, y } = coord;
    let x1 = x;
    let y1 = y;
    if (coord.sys === "court") {
        const physics = convert.localization2physics({ x, y, z: 1000 });

        x1 = physics.x;
        y1 = physics.y;
    }

    const projected = {
        x: x1 * scaled.widthPPM + scaled.xOffset,
        y:
            y1 >= 0
                ? scaled.yOffset - y1 * scaled.heightPPM
                : Math.abs(y1 * scaled.heightPPM) + scaled.yOffset,
    };

    return projected;
}

export function image2Projected(coord: Coord, scaled: ScaledDimensions): Coord {
    const { x, y } = coord;
    return {
        x: (x - scaled.xOffset) / scaled.widthPPM,
        y: (scaled.yOffset - y) / scaled.heightPPM,
    };
}

export function playerSide(coord: Coord, sport: Sport): Side {
    if (sport === "PLATFORM_TENNIS" && coord.x <= COURT_WIDTH_MM / 2) {
        return "deuce";
    }

    if (coord.x < 0) {
        return "deuce";
    }
    return "ad";
}

export function sideKeys(
    coord: Coord,
    sport: Sport,
): { adKey: PlayMode; deuceKey: PlayMode } {
    const side = playerSide(coord, sport);
    if (side === "deuce") {
        return { adKey: "mirror", deuceKey: "standard" };
    }
    return { adKey: "standard", deuceKey: "mirror" };
}

export function mirrorShot<T extends SingleShotShot | CuratedWorkoutShot>(
    shot: T,
    manualTrim = 0,
): T {
    const { pan } = shot;
    return {
        ...shot,
        pan: -pan + manualTrim,
    };
}

export function mirrorPlayer(coord: Coord, sport: Sport): CoordWithSys {
    const { x, y } = coord;

    // We're assuming positions are still in old mm system for platform tennis
    if (sport === "PLATFORM_TENNIS") {
        return {
            y,
            x: COURT_WIDTH_MM - x,
            sys: "court",
        };
    }
    return {
        y,
        x: -x,
        sys: "physics",
    };
}

/**
 * The player's mirrored position for a drill the machine plays from where it
 * already stands.
 *
 * `mirrorPlayer` reflects about the field's centreline, which is right for
 * "mirror": there the whole scene reflects, the machine included - see
 * mirrorTrainer, and useAppWorkoutPlay which moves it before play. "dual" is
 * different. The machine stays put and only the pan sign alternates, and pan is
 * measured from the machine's own heading, so the axis the SHOTS reflect about
 * is the ray it is pointed down - not x = 0.
 *
 * The two axes coincide only for a machine on the centreline facing straight
 * down it, which for the racket sports is guaranteed by isCenterWorkout gating
 * the "both" button to within 0.61 m of centre. Lacrosse has no such gate: its
 * spots are goal-relative, 25 of them, most off-centre and angled at the cage.
 * Reflecting the player about x = 0 there puts them somewhere the mirrored
 * shots never go.
 *
 * Yaw is radians from +y toward +x, the convention lacrosseDefaultYaw stores.
 */
export function mirrorPlayerAboutAim(
    coord: Coord,
    trainer: { x: number; y: number; yaw: number },
): CoordWithSys {
    // Unit vector along the machine's heading.
    const dx = Math.sin(trainer.yaw);
    const dy = Math.cos(trainer.yaw);

    // The player relative to the machine, split into the part along the aim
    // (kept) and the part across it (negated).
    const vx = coord.x - trainer.x;
    const vy = coord.y - trainer.y;
    const along = vx * dx + vy * dy;

    return {
        x: trainer.x + 2 * along * dx - vx,
        y: trainer.y + 2 * along * dy - vy,
        sys: "physics",
    };
}

/**
 * The mirrored player position to draw for "dual".
 *
 * Lacrosse reflects about where the machine is pointed; every other sport keeps
 * the centreline reflection it has always used, which is equivalent for them
 * because "both" is only offered from the centre.
 */
export function mirrorPlayerForDual(
    coord: Coord,
    trainer: { x: number; y: number; yaw: number } | undefined,
    sport: Sport,
): CoordWithSys {
    if (sport !== "LACROSSE" || !trainer) {
        return mirrorPlayer(coord, sport);
    }
    return mirrorPlayerAboutAim(coord, trainer);
}

export function mirrorTrainer(
    position: PositionWithHeight,
    sport: Sport,
): PositionWithHeight {
    const { x, y, yaw, heightIn } = position;

    // We're assuming positions are still in old mm system for platform tennis
    if (sport === "PLATFORM_TENNIS") {
        return {
            y,
            heightIn,
            yaw: -yaw,
            x: COURT_WIDTH_MM - x,
        };
    }
    return {
        y,
        heightIn,
        yaw: -yaw,
        x: -x,
    };
}

const ALLOWED_DIFF = 0.61; // 2 feet from center line in meters
export function isCenterWorkout(positionX: number, sport: Sport) {
    if (
        sport === "PLATFORM_TENNIS" &&
        Math.abs(positionX - COURT_WIDTH_MM / 2) < ALLOWED_DIFF * 1000
    ) {
        return true;
    }

    if (sport !== "PLATFORM_TENNIS" && Math.abs(positionX) < ALLOWED_DIFF) {
        return true;
    }

    return false;
}

/**
 * May a player stand at this point, for this sport?
 *
 * The racket sports are played across a net on physics y = 0 with the machine
 * on the near side, so a player position with y <= 0 is a mis-click on the
 * trainer's own half and is dropped rather than sent. That rule is a fact
 * about a NET, not about the picker, and lacrosse has no net: its origin is
 * the goal line, the machine stands downfield of it at positive y, and the
 * whole area BEHIND the cage - negative y, where a keeper stands and where
 * shots are backed up - is legitimately playable.
 *
 * Stated per sport rather than "lacrosse is special" so the next open-field
 * sport is one entry rather than a second special case.
 */
export function playerPointIsSelectable(
    sport: Sport,
    point: { y: number },
): boolean {
    return sport === "LACROSSE" || point.y > 0;
}
